import Client from "./client";
import {pageParams} from "./paging";

/**
 * One page of a project's bugs, newest first.
 *
 * `filters` ({type, status, open}) go to the server rather than being applied to
 * the page here, which is the point: filtering a page in the browser shows the
 * matching bugs of page one instead of the first page of matching bugs, and
 * silently loses everything past the boundary. `cursor` is the previous page's
 * `nextCursor`, opaque and handed back unchanged.
 *
 * `total` only comes back for the first page of a given set of filters - the
 * server doesn't recount on every scroll - so a caller keeps the one it was
 * given while appending.
 */
export const GetBugs = ({projectId, accessToken, filters, limit, cursor}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/bugs`, {
            params: pageParams({filters, limit, cursor}),
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve({
                items: res.data.bugs,
                nextCursor: res.data.nextCursor,
                hasMore: res.data.hasMore,
                total: res.data.total,
            });
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetBug = ({bugId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/bugs/${bugId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bug);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// The same bug, addressed by this project's own number rather than its
// database id - see Tasks.js's GetTicketByNumber for why.
export const GetBugByNumber = ({projectId, bugNumber, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/bugs/number/${bugNumber}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bug);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateBug = ({projectId, summary, description, reproductionSteps, expectedResult, actualResult, bugTemplateId, fieldValues, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/bugs`, {summary, description, reproductionSteps, expectedResult, actualResult, bugTemplateId, fieldValues}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bug);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// `version`/`base` are how the API tells a save that changed something else
// touched in the meantime from one that's a real conflict - see PUT
// /bugs/:id. `base` only needs to carry the fields actually being sent
// (their value as this client originally loaded the bug), not the whole
// record.
export const UpdateBug = ({bugId, summary, description, reproductionSteps, expectedResult, actualResult, bugTemplateId, fieldValues, open, status, version, base, accessToken}) => {
    const data = {};
    if (summary !== undefined) data.summary = summary;
    if (description !== undefined) data.description = description;
    if (reproductionSteps !== undefined) data.reproductionSteps = reproductionSteps;
    if (expectedResult !== undefined) data.expectedResult = expectedResult;
    if (actualResult !== undefined) data.actualResult = actualResult;
    if (bugTemplateId !== undefined) data.bugTemplateId = bugTemplateId;
    if (fieldValues !== undefined) data.fieldValues = fieldValues;
    if (open !== undefined) data.open = open;
    if (status !== undefined) data.status = status;
    if (version !== undefined) data.version = version;
    if (base !== undefined) data.base = base;

    return new Promise((resolve, reject) => {
        Client.put(`/api/bugs/${bugId}`, data, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bug);
        }).catch((e) => {
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}

// At least one of projectId/organizationId is required by the API - pass
// whichever one(s) apply to the current view.
export const GetBugTemplate = ({projectId, organizationId, accessToken}) => {
    return new Promise((resolve, reject) => {
        const params = {};
        if (projectId !== undefined) params.projectId = projectId;
        if (organizationId !== undefined) params.organizationId = organizationId;

        Client.get('/api/bug-templates', {
            params,
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bugTemplates);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateBugTemplate = ({projectId, type, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/bug-templates`, {type}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bugTemplate);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const RenameBugTemplate = ({templateId, type, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/bug-templates/${templateId}`, {type}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bugTemplate);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const DeleteBugTemplate = ({templateId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/bug-templates/${templateId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateBugField = ({templateId, label, type, placeholder, data, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/bug-templates/${templateId}/fields`, {label, type, placeholder, data}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bugField);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateBugField = ({fieldId, label, type, placeholder, data, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/bug-fields/${fieldId}`, {label, type, placeholder, data}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.bugField);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const DeleteBugField = ({fieldId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/bug-fields/${fieldId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}
